/**
 * Pricing policy — the rounding rule the live site used to fix by accident, and
 * which `packages/db/src/money.ts` flags as an OWNER DECISION. The engine
 * implements both sides of it, so nothing a customer is quoted changes until
 * the owner flips it on purpose.
 *
 * The default is not asserted to be correct — it is asserted to be UNCHANGED.
 * Flipping it is a one-line edit here plus re-pinning the parity tests; do not
 * let a refactor settle it by silently reformulating the math.
 *
 * There was a second switch here, `multiServiceScope`, naming what the
 * MULTI_SERVICE basket bonus multiplied. It switched nothing: the bonus itself
 * was removed from the engine (see `CartLine` in `pricing-engine.ts` — „nothing
 * is keyed to it any more"), so the field was a setting with no reader under a
 * comment that told the next reader how the basket is discounted. Anybody
 * checking the margin would have counted a discount the system stopped
 * applying.
 */

/**
 * How a discounted or VAT-inflated total is rounded.
 *
 *  - `whole-zloty` — round to the nearest whole złoty (…× 100 grosze). What the
 *    live site does: `Math.round(base_zł × factor)` in `bundle.ts` / `rental.ts`
 *    / `lib/order-money.ts`, and `Math.round(net_zł × 1.23)` in `tiers.ts`. Both
 *    operate on whole-złoty inputs, so the result lands on a whole złoty.
 *  - `grosz-exact` — round to the nearest grosz. The natural computation over a
 *    schema that stores grosze and rungs in basis points. More defensible, and
 *    a DIFFERENT published price: measured in `money.ts`, 33 of 72 volume
 *    (tier, qty) pairs and 9 of 9 VAT-inclusive tier prices diverge, by −50…+47
 *    grosze.
 */
export type RoundingMode = 'whole-zloty' | 'grosz-exact'

export interface PricingPolicy {
  readonly rounding: RoundingMode
}

/**
 * The house policy (spec §14 · H2 · decision #7).
 *
 * `rounding` is `grosz-exact` as of H2, and it is the other half of the same
 * decision (#7). The site rounded every discounted total to the nearest whole
 * złoty, which is what an arithmetic done in złoty floats falls into rather
 * than something anybody chose. A schema that stores grosze and rungs in basis
 * points has a natural answer, and an invoice has to agree with a payment to
 * the grosz — spec §18's document cannot state a figure the transfer does not
 * match.
 *
 * What it moves, measured over the real rate card (89 / 149 / 219 / 299 / 399 /
 * 449 / 125 / 89,50 zł):
 *
 *     VOLUME   54 of 96 (rate, quantity) pairs move, by −50…+45 gr
 *     RENTAL   81 of 112 (rate, days) pairs move, by −50…+38 gr
 *     VAT      8 of 8 gross prices move, by −25…+47 gr
 *
 * Half a złoty at the outside, in both directions, and it nets out near zero
 * over a basket. Nothing anybody notices on one order; everything on a ledger
 * that has to reconcile.
 */
export const DEFAULT_PRICING_POLICY: PricingPolicy = {
  rounding: 'grosz-exact',
}

/**
 * Polish VAT on creative services, basis points.
 *
 * RE-EXPORTED, not restated. It was declared here as well as in
 * `@repo/db/schema/columns`, with a comment saying it "mirrors" the other —
 * which is a promise nothing checked, and the kind of promise that is kept
 * until the day a rate changes. The column default is the original, because
 * that is the value a row gets when nobody says otherwise.
 */
export { DEFAULT_TAX_RATE_BPS } from '@repo/db/schema/columns'

/** One rung of a discount ladder, as the engine consumes it (from the DB). */
export interface LadderStep {
  /** Read in the ladder's own unit: pieces (VOLUME), days (RENTAL_DAYS), distinct services (MULTI_SERVICE). */
  readonly threshold: number
  /** Basis points off (1000 = 10%). Only non-zero rungs are stored. */
  readonly discountBps: number
}
